import useSWRInfinite, { SWRInfiniteResponse } from 'swr/infinite'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  getAuthorizationCacheScope,
  getRpcAccess,
  isPermissionDenied,
  RpcAccessDenied,
} from '../../app/AppAuth/rpcAuthorization'
import { useAuth, useAuthHeader } from '../../context/authContext'
import { getRefererHeader } from '../../adapter/referer'
import { callGrpc, SwrGrpcResponse, AuthorizedQueryState } from './useSwrGrpc'
import { GrpcCall, useGetInfiniteCacheKey } from './utils'
import { queue } from './queue'

/**
  Hook for using SWR with pagination
  For an example of working with this hook see useChangeLogEntries.tsx
  ** This does not include logic for audit specific request. You will have to handle that in your request callback function
  * @param {GrpcCall} asyncCall - the grpc method that you want to call
  * @param {function requestFunc(pageNumber:number) {
    return Request
  }} - callback function that returns the request with the passed in page number added. See useChangeLogEntries.tsx
  * @param {SWRConfiguration} config - optional with options like disabling revalidation on focus. See SWR docs
  * @returns {SWRInfiniteResponse} { response }
*/

export const useSwrInfiniteGrpc = <Request, Response>(
  asyncCall: GrpcCall<Request, Response>,
  requestFunc: () => Request,
  pageNumber: number,
  config?: SwrGrpcResponse,
  shouldFetch = true,
): { response: SWRInfiniteResponse<Response> & AuthorizedQueryState } => {
  const auth = useAuth()
  const access = getRpcAccess(asyncCall, auth, requestFunc())
  const enabled = shouldFetch && access === 'allowed'
  const scope = getAuthorizationCacheScope(auth)
  const current = useRef({ auth, enabled, scope })
  current.current = { auth, enabled, scope }
  useEffect(() => {
    current.current.enabled = enabled
    return () => {
      current.current.enabled = false
    }
  }, [enabled])
  const denied = useMemo(() => new RpcAccessDenied(asyncCall), [asyncCall])
  const header = {
    ...useAuthHeader(),
    ...getRefererHeader(),
  }
  const getInfiniteCacheKey = useGetInfiniteCacheKey()
  const response = useSWRInfinite<Response>(
    () =>
      enabled
        ? getInfiniteCacheKey(asyncCall, requestFunc(), pageNumber)
        : null,
    (_method: string, _request: unknown, _accountId: string) =>
      queue.add(async () => {
        const request = requestFunc()
        if (
          !current.current.enabled ||
          current.current.scope !== scope ||
          getRpcAccess(asyncCall, current.current.auth, request) !== 'allowed'
        )
          throw denied
        const data = await callGrpc(
          asyncCall,
          request,
          header,
          current.current.auth,
        ).catch((error: unknown) => {
          if (!current.current.enabled || current.current.scope !== scope)
            throw denied
          throw error
        })
        if (!current.current.enabled || current.current.scope !== scope)
          throw denied
        return data
      }) as Promise<Response>,
    {
      ...config,
      keepPreviousData: false,
      shouldRetryOnError: (error) => {
        if (
          isPermissionDenied(error) ||
          !current.current.enabled ||
          current.current.scope !== scope
        )
          return false
        return typeof config?.shouldRetryOnError === 'function'
          ? config.shouldRetryOnError(error)
          : config?.shouldRetryOnError !== false
      },
    },
  )
  const { mutate: swrMutate } = response
  const mutate = useCallback(
    (...args: Parameters<typeof swrMutate>) => {
      if (!current.current.enabled || current.current.scope !== scope)
        return Promise.resolve(undefined)
      return swrMutate(...args)
    },
    [swrMutate, scope],
  ) as typeof swrMutate
  return {
    response: {
      ...response,
      data: enabled ? response.data : undefined,
      access,
      isDisabled: !shouldFetch || access === 'denied',
      error: enabled ? response.error : undefined,
      isLoading:
        shouldFetch && access === 'pending'
          ? true
          : enabled && response.isLoading,
      isValidating: enabled && response.isValidating,
      mutate,
      setSize: (...args) =>
        current.current.enabled && current.current.scope === scope
          ? response.setSize(...args)
          : Promise.resolve(undefined),
    },
  }
}
